import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Box, Button, Typography } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningPage from './WarningPage';
import useWarningMonth from './useWarningMonth';
import ThresholdNote from './ThresholdNote';
import RetryNotice from './RetryNotice';
import { departmentLabel } from './departments';
import WarningLoading from './WarningLoading';
import FlagSummary from './FlagSummary';
import apiRequest from '../../api/request';
import WarningMetricTable from './WarningMetricTable';
import useConfigAccess from './useConfigAccess';

const DEPARTMENTS = [
  { id: 11, name: 'OGD' },
  { id: 12, name: 'CSD' },
  { id: 3, name: 'Foundation' },
  { id: 20, name: 'Classroom' },
  { id: 21, name: 'Little Genie' },
  { id: 22, name: 'Speak Pro' },
  { id: 35, name: 'SkillX' },
];
const departmentName = (id) => departmentLabel(id, DEPARTMENTS);

// Everyone credited with collections is listed; only people with a Target Revenue have something to compare it with. The rest come back
// with targetRevenue null: shown with what they collected, "No target set", and never flagged.
const hasNoTarget = (row) => row.targetRevenue === null || row.targetRevenue === undefined;

const StatusCell = ({ isWarning }) =>
  isWarning ? (
    <WarningAmberIcon fontSize="small" sx={{ color: 'error.main', display: 'block', mx: 'auto' }} />
  ) : (
    <CheckCircleOutlineIcon fontSize="small" sx={{ color: 'success.main', display: 'block', mx: 'auto' }} />
  );

const FeeCollectionDashboard = () => {
  // The month is shared across the whole dashboard and remembered for the session (useWarningMonth.js).
  const [month, setMonth] = useWarningMonth();
  const [rows, setRows] = useState([]);
  const [thresholdNote, setThresholdNote] = useState(null); // why there is no threshold for this month, when there isn't
  const [summary, setSummary] = useState({ totalEvaluated: 0, warningCount: 0, noTargetCount: 0, collectionThresholdPercent: null });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0); // Retry re-runs just this request

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    apiRequest({ url: `warning-dashboard/fee-collection-dashboard?month=${month.format('YYYY-MM-DD')}`, method: 'get' })
      .then((res) => {
        if (cancelled) return;
        // apiRequest resolves even on a failed call (src/api/request.js) - without this check a server error
        // rendered as an empty page (or zeros) instead of a message
        if (res?.status !== 'success') {
          setError(res?.message || 'Could not load Fee Collection results');
          return;
        }
        setRows(res?.data?.rows || []);
        setThresholdNote(res?.data?.thresholdNote || null);
        setSummary({
          totalEvaluated: res?.data?.totalEvaluated || 0,
          warningCount: res?.data?.warningCount || 0,
          noTargetCount: res?.data?.noTargetCount || 0,
          collectionThresholdPercent: res?.data?.collectionThresholdPercent ?? null,
        });
        setError(null);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.response?.data?.message || 'Could not load Fee Collection results');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [month, reloadKey]);

  const dangerScore = (row) => (row.requiredCollection === null ? -Infinity : row.requiredCollection - row.revenue);

  const sortedRows = useMemo(
    () => [...rows].sort((a, b) => dangerScore(b) - dangerScore(a)),
    [rows]
  );

  const columns = [
    { field: 'status', headerName: 'Status', align: 'center', render: (row) => (hasNoTarget(row) ? <Typography variant="body2" color="text.disabled" align="center" title="No target for this month, so there is nothing to compare the collection with">-</Typography> : <StatusCell isWarning={row.isWarning} />), csvValue: (row) => (hasNoTarget(row) ? 'Not evaluated' : row.isWarning ? 'Warning' : 'OK') },
    {
      field: 'entityName',
      headerName: 'Name',
      render: (row) => (
        <Typography
          variant="body2"
          fontStyle={row.isActive === false ? 'italic' : 'normal'}
          color={row.isActive === false || row.isWarning ? 'error.main' : 'text.primary'}
        >
          {row.entityName}
          {row.isActive === false && ' (Inactive)'}
        </Typography>
      ),
    },
    { field: 'departmentId', headerName: 'Department', render: (row) => departmentName(row.departmentId), csvValue: (row) => departmentName(row.departmentId) },
    { field: 'targetRevenue', headerName: 'Target Revenue', align: 'right', render: (row) => (hasNoTarget(row) ? <Typography variant="body2" color="text.secondary" title="This person has no Target Revenue for the month, so there is no required collection - they are shown with what they collected but never flagged.">No target set</Typography> : `₹${Number(row.targetRevenue).toLocaleString()}`), csvValue: (row) => (hasNoTarget(row) ? 'No target set' : row.targetRevenue) },
    {
      field: 'revenue',
      headerName: 'Collected',
      align: 'right',
      render: (row) => (
        <Typography variant="body2" fontWeight={row.isWarning ? 700 : 400} color={row.isWarning ? 'error.main' : 'text.primary'}>
          ₹{Number(row.revenue).toLocaleString()}
        </Typography>
      ),
      csvValue: (row) => row.revenue,
    },
    {
      field: 'requiredCollection',
      headerName: 'Required Collection',
      align: 'right',
      render: (row) => (hasNoTarget(row) ? <Typography variant="body2" color="text.disabled">-</Typography> : row.requiredCollection === null ? <Typography variant="body2" color="text.secondary">Not configured</Typography> : `₹${Number(row.requiredCollection).toLocaleString()}`),
      csvValue: (row) => (hasNoTarget(row) ? 'No target set' : row.requiredCollection === null ? 'Not configured' : row.requiredCollection),
    },
  ];

  const { canConfigure } = useConfigAccess();

  return (
    <WarningPage
      title="Fee Collection"
      actions={
        <Box display="flex" gap={1}>
          <Button component={Link} to={`/dashboard/warning-dashboard?month=${month.format('YYYY-MM-DD')}`} variant="text">
            Back to Warning Dashboard
          </Button>
          {canConfigure('fee-collection-config') && (
            <Button component={Link} to={`/dashboard/warning-dashboard/fee-collection-config?month=${month.format('YYYY-MM-DD')}`} variant="outlined">
              Configure Threshold
            </Button>
          )}
        </Box>
      }
    >
      <Box display="flex" alignItems="center" gap={2} sx={{ mb: 2 }}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker views={['year', 'month']} label="Month" value={month} onChange={(v) => v && setMonth(v)} />
        </LocalizationProvider>
      </Box>

      {loading && <WarningLoading />}

      {!loading && error && <RetryNotice message={error} onRetry={() => setReloadKey((k) => k + 1)} />}

      {!loading && !error && (
        <FlagSummary warningCount={summary.warningCount} totalEvaluated={summary.totalEvaluated}>
            {summary.collectionThresholdPercent !== null && ` · required = target × ${summary.collectionThresholdPercent}%`}
            {summary.noTargetCount > 0 && ` · ${summary.noTargetCount} have no target, so nothing to compare`}
          </FlagSummary>
      )}

      {!loading && !error && <ThresholdNote note={thresholdNote} month={month} configPath="fee-collection-config" canSet={canConfigure('fee-collection-config')} />}

      {!loading && !error && (
        <WarningMetricTable
          rows={sortedRows}
          columns={columns}
          departmentName={departmentName}
          emptyMessage="No collections were recorded for this period."
          exportFileName="Fee_Collection"
        />
      )}
    </WarningPage>
  );
};

export default FeeCollectionDashboard;
